import { labelWithSpecies } from 'common/utils/species'
import { CRUDAction, FeatureKey } from 'common/constants/permission'
import useMedia from 'common/hooks/useMedia'
import { useCurrentCycleInfo, useSpeciesName } from 'modules/pond/api/selector'
import { usePermitToActionFeature } from 'modules/user/api/selector'
import { FC, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useRecoilState } from 'recoil'

import AddIcon from '@mui/icons-material/Add'
import { Box, Button, Card, Typography, Grid } from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'

import { shrimpTransferDateTableFilterState } from '../../stores/atom'
import ShrimpTransferRecordDialog from '../ShrimpTransferRecordDialog'

interface ShrimpTransfersRecordCardProps {
  children: React.ReactNode
}

const ShrimpTransfersRecordCard: FC<ShrimpTransfersRecordCardProps> = ({ children }) => {
  const { t } = useTranslation('shrimpTransfer')
  const speciesName = useSpeciesName()
  const showRecordBtn = usePermitToActionFeature([CRUDAction.Create], FeatureKey.ShrimpTransfer)
  const [open, setOpen] = useState(false)
  const [date, setDate] = useRecoilState(shrimpTransferDateTableFilterState)
  const isMobile = useMedia('sm')

  const cycleInfo = useCurrentCycleInfo()
  const isCompleted = cycleInfo?.status === 'completed'

  const handleOpen = () => {
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
  }

  const handleChangeDate = (value: Date | null, _: any) => {
    if (!value) {
      setDate(undefined)
      return
    }

    setDate(value)
  }

  useEffect(() => {
    return () => {
      setDate(undefined)
    }
  }, [setDate])

  return (
    <Card sx={{ p: 2, height: '100%' }}>
      <Grid
        container
        spacing={2}
        alignItems={isMobile ? undefined : 'center'}
        justifyContent="space-between"
        sx={{ mb: 5 }}
      >
        <Grid item xs={12} sm="auto" sx={{ width: isMobile ? '100%' : 'auto' }}>
          <Typography variant="h5">{labelWithSpecies(t('transfer.title'), speciesName)}</Typography>
          {isMobile && showRecordBtn && !isCompleted && (
            <Box sx={{ mt: 2 }}>
              <Button variant="contained" onClick={handleOpen} startIcon={<AddIcon />} fullWidth>
                {labelWithSpecies(t('transfer.records'), speciesName)}
              </Button>
            </Box>
          )}
        </Grid>
        <Grid
          item
          xs={12}
          sm
          sx={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            gap: 2,
            alignItems: isMobile ? 'stretch' : 'center',
            width: isMobile ? '100%' : 'auto',
            justifyContent: isMobile ? undefined : 'flex-end',
          }}
        >
          <DatePicker
            label={t('transfer.dateSearch')}
            value={date}
            format="dd/MM/yyyy"
            onChange={handleChangeDate}
            slotProps={{
              textField: {
                size: 'small',
              },
              actionBar: {
                actions: ['clear', 'accept'],
              },
            }}
          />
          {!isMobile && showRecordBtn && !isCompleted && (
            <Button variant="contained" onClick={handleOpen} startIcon={<AddIcon />}>
              {labelWithSpecies(t('transfer.records'), speciesName)}
            </Button>
          )}
        </Grid>
      </Grid>
      {children}
      <ShrimpTransferRecordDialog open={open} onClose={handleClose} mode="create" />
    </Card>
  )
}

export default ShrimpTransfersRecordCard
